import { EventType, type ExperimentEvent } from './Events.ts';

/** The stages the pipeline is decomposed into, in order. */
export const LATENCY_STAGES = [
  'decision_to_request',
  'request_to_stim',
  'stim_to_input',
  'input_to_flap',
  'total',
] as const;

export type LatencyStage = (typeof LATENCY_STAGES)[number];

export const STAGE_LABELS: Record<LatencyStage, string> = {
  decision_to_request: 'decision → stim request',
  request_to_stim: 'stim request → stim event',
  stim_to_input: 'stim event → physical input',
  input_to_flap: 'physical input → game flap',
  total: 'total (decision → flap)',
};

export interface LatencyStats {
  stage: LatencyStage;
  count: number;
  current: number | null;
  mean: number;
  median: number;
  p95: number;
  /** Sample standard deviation, ms. The usual definition of "jitter" here. */
  jitter: number;
  min: number;
  max: number;
}

interface Chain {
  requestId: string;
  decision?: number;
  request?: number;
  stimStarted?: number;
  stimEnded?: number;
  buttonDown?: number;
  flap?: number;
  resolved: boolean;
  outcome?: 'executed' | 'missed' | 'rejected' | 'failed';
}

export interface ChainSummary {
  requestId: string;
  outcome: Chain['outcome'];
  stages: Partial<Record<LatencyStage, number>>;
}

/**
 * Reconstructs intent -> flap chains from the event stream and measures where
 * the time goes.
 *
 * It is a pure consumer of `ExperimentEvent`s -- feed it the live logger or a
 * loaded session file and it produces the same numbers. Nothing else in the
 * system needs to know how latency is defined.
 */
export class LatencyTracker {
  private chains = new Map<string, Chain>();
  private samples: Record<LatencyStage, number[]> = emptySamples();
  private latest: Record<LatencyStage, number | null> = {
    decision_to_request: null,
    request_to_stim: null,
    stim_to_input: null,
    input_to_flap: null,
    total: null,
  };
  private completed: ChainSummary[] = [];

  /** Rolling window; older samples are dropped so stats track the current setup. */
  constructor(private readonly windowSize = 200) {}

  reset(): void {
    this.chains.clear();
    this.samples = emptySamples();
    this.completed = [];
    for (const s of LATENCY_STAGES) this.latest[s] = null;
  }

  ingest(event: ExperimentEvent): void {
    const id = event.requestId;
    if (!id) return;
    const chain = this.chains.get(id) ?? { requestId: id, resolved: false };
    this.chains.set(id, chain);

    switch (event.type) {
      case EventType.BOT_FLAP_REQUESTED:
        chain.decision = event.timestamp;
        break;
      case EventType.STIM_REQUESTED:
        chain.request = event.timestamp;
        this.record('decision_to_request', chain.decision, chain.request);
        break;
      case EventType.STIM_STARTED:
        chain.stimStarted = event.timestamp;
        this.record('request_to_stim', chain.request, chain.stimStarted);
        break;
      case EventType.STIM_ENDED:
        chain.stimEnded = event.timestamp;
        break;
      case EventType.BUTTON_DOWN:
        chain.buttonDown = event.timestamp;
        this.record('stim_to_input', chain.stimStarted ?? chain.request, chain.buttonDown);
        break;
      case EventType.FLAP_EXECUTED:
        chain.flap = event.timestamp;
        this.record('input_to_flap', chain.buttonDown, chain.flap);
        this.record('total', chain.decision, chain.flap);
        this.finish(chain, 'executed');
        break;
      case EventType.STIM_MISSED:
        this.finish(chain, 'missed');
        break;
      case EventType.STIM_REJECTED:
        this.finish(chain, 'rejected');
        break;
      case EventType.STIM_FAILED:
        this.finish(chain, 'failed');
        break;
      default:
        break;
    }
  }

  private record(stage: LatencyStage, from: number | undefined, to: number | undefined): void {
    if (from === undefined || to === undefined) return;
    const dt = to - from;
    if (!Number.isFinite(dt) || dt < 0) return;
    const bucket = this.samples[stage];
    bucket.push(dt);
    if (bucket.length > this.windowSize) bucket.shift();
    this.latest[stage] = dt;
  }

  private finish(chain: Chain, outcome: NonNullable<Chain['outcome']>): void {
    if (chain.resolved) return;
    chain.resolved = true;
    chain.outcome = outcome;
    const stages: Partial<Record<LatencyStage, number>> = {};
    if (chain.decision !== undefined && chain.request !== undefined)
      stages.decision_to_request = chain.request - chain.decision;
    if (chain.request !== undefined && chain.stimStarted !== undefined)
      stages.request_to_stim = chain.stimStarted - chain.request;
    if (chain.stimStarted !== undefined && chain.buttonDown !== undefined)
      stages.stim_to_input = chain.buttonDown - chain.stimStarted;
    if (chain.buttonDown !== undefined && chain.flap !== undefined)
      stages.input_to_flap = chain.flap - chain.buttonDown;
    if (chain.decision !== undefined && chain.flap !== undefined)
      stages.total = chain.flap - chain.decision;
    this.completed.push({ requestId: chain.requestId, outcome, stages });
    if (this.completed.length > this.windowSize) this.completed.shift();
    this.chains.delete(chain.requestId);
  }

  stats(stage: LatencyStage): LatencyStats {
    return summarise(stage, this.samples[stage], this.latest[stage]);
  }

  allStats(): LatencyStats[] {
    return LATENCY_STAGES.map((s) => this.stats(s));
  }

  /** Median total latency, the number the bot should be compensating for. */
  medianTotal(): number | null {
    const s = this.stats('total');
    return s.count > 0 ? s.median : null;
  }

  outcomes(): Record<string, number> {
    const out: Record<string, number> = { executed: 0, missed: 0, rejected: 0, failed: 0 };
    for (const c of this.completed) if (c.outcome) out[c.outcome]++;
    return out;
  }

  chainSummaries(): readonly ChainSummary[] {
    return this.completed;
  }

  /** Chains still waiting for a response. */
  get openChains(): number {
    return this.chains.size;
  }
}

function emptySamples(): Record<LatencyStage, number[]> {
  return {
    decision_to_request: [],
    request_to_stim: [],
    stim_to_input: [],
    input_to_flap: [],
    total: [],
  };
}

export function summarise(
  stage: LatencyStage,
  samples: readonly number[],
  current: number | null,
): LatencyStats {
  if (samples.length === 0) {
    return {
      stage, count: 0, current, mean: NaN, median: NaN, p95: NaN,
      jitter: NaN, min: NaN, max: NaN,
    };
  }
  const sorted = [...samples].sort((a, b) => a - b);
  const mean = samples.reduce((a, b) => a + b, 0) / samples.length;
  const variance =
    samples.length > 1
      ? samples.reduce((a, b) => a + (b - mean) ** 2, 0) / (samples.length - 1)
      : 0;
  return {
    stage,
    count: samples.length,
    current,
    mean,
    median: quantile(sorted, 0.5),
    p95: quantile(sorted, 0.95),
    jitter: Math.sqrt(variance),
    min: sorted[0],
    max: sorted[sorted.length - 1],
  };
}

/** Linear-interpolated quantile of an already-sorted array. */
export function quantile(sorted: readonly number[], q: number): number {
  if (sorted.length === 0) return NaN;
  if (sorted.length === 1) return sorted[0];
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}
